import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";
import { Calendar, Loader2, MapPin, ShieldCheck, Trash2, User } from "lucide-react";

import { getJson, http } from "@/lib/http";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
    canSubmitHeldRelease,
    createHeldReviewFormDefaults,
    DEFAULT_HELD_RELEASE_REASON,
    isHeldReleaseTargetAllowed,
    resolveDefaultHeldAssignmentId,
} from "./heldEvidenceReviewModalLogic";
import {
    HELD_EVIDENCE_AWAITING_UPLOAD_INTRO,
    HELD_EVIDENCE_AWAITING_UPLOAD_NEXT,
    HELD_EVIDENCE_DECISION_ABANDON,
    HELD_EVIDENCE_DECISION_RELEASE,
    HELD_EVIDENCE_PREVIEW_PENDING,
    HELD_EVIDENCE_RELEASE_HELP,
    HELD_EVIDENCE_WHY_HELD_INTRO,
    describeHeldEvidenceOperatorFacts,
    describeHeldEvidenceSituation,
    heldEvidenceBlockerMessage,
    heldEvidenceDisplayCampaignName,
    heldEvidenceModalTitle,
    heldEvidenceReviewStatusLabel,
    heldEvidenceUploadBadgeLabel,
    type HeldEvidenceProposedTask,
} from "@shared/held-evidence-review-labels";

type HeldEvidenceDetail = {
    mediaOperationId: string;
    operatorId: number;
    operatorName: string;
    storeId: number;
    storeName: string;
    cycleId: number;
    campaignName: string;
    taskId: number | null;
    assignmentId: number | null;
    proposedAssignmentId: number | null;
    heldStatus: "awaiting_upload" | "awaiting_release" | "needs_assignment" | "beyond_sla";
    bytesServerSafe: boolean;
    reconciliationStatus: string;
    automaticBindingBlocker: string | null;
    timestamp: string;
    capturedAt: string;
    uploadedAt: string | null;
    imageUrl: string | null;
    operationState: string | null;
    lastErrorCode: string | null;
    lastErrorMessage: string | null;
    proposedTask?: HeldEvidenceProposedTask;
    releaseTargets: Array<{
        assignmentId: number;
        campaignId: number;
        campaignName: string;
        status: string;
        submissionOperationId: string | null;
        recommended: boolean;
    }>;
};

interface HeldEvidenceReviewModalProps {
    mediaOperationId: string | null;
    onClose: () => void;
}

export default function HeldEvidenceReviewModal({
    mediaOperationId,
    onClose,
}: HeldEvidenceReviewModalProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [releaseReason, setReleaseReason] = useState(DEFAULT_HELD_RELEASE_REASON);
    const [abandonReason, setAbandonReason] = useState("");
    const [abandonOpen, setAbandonOpen] = useState(false);
    const [selectedAssignmentId, setSelectedAssignmentId] = useState<string>("");

    React.useEffect(() => {
        if (!mediaOperationId) return;
        const defaults = createHeldReviewFormDefaults();
        setReleaseReason(defaults.releaseReason);
        setAbandonReason(defaults.abandonReason);
        setAbandonOpen(defaults.abandonOpen);
        setSelectedAssignmentId(defaults.selectedAssignmentId);
    }, [mediaOperationId]);

    const { data, isLoading, error } = useQuery<{ success: boolean; data: HeldEvidenceDetail }>({
        queryKey: ["/api/review/held", mediaOperationId],
        queryFn: async () => getJson(`/api/review/held/${mediaOperationId}`),
        enabled: !!mediaOperationId,
    });

    const detail = data?.data;

    const proposedTask = detail?.proposedTask ?? (detail ? {
        assignmentId: detail.proposedAssignmentId,
        campaignName: detail.campaignName !== 'Held evidence' && detail.campaignName !== 'Unknown task'
            ? detail.campaignName
            : (
                detail.releaseTargets.find(
                    target => target.assignmentId === detail.proposedAssignmentId,
                )?.campaignName ?? null
            ),
        isAvailableForRelease: detail.releaseTargets.some(
            target => target.assignmentId === detail.proposedAssignmentId,
        ),
    } : null);

    React.useEffect(() => {
        if (!detail) return;
        setSelectedAssignmentId(resolveDefaultHeldAssignmentId(detail));
    }, [detail?.mediaOperationId, detail?.releaseTargets, detail?.proposedAssignmentId, detail?.assignmentId]);

    const invalidateReviewQueries = async () => {
        await queryClient.invalidateQueries({ queryKey: ["/api/review/pending"] });
        await queryClient.invalidateQueries({ queryKey: ["/api/review/held", mediaOperationId] });
    };

    const releaseMutation = useMutation({
        mutationFn: async () => {
            const targetAssignmentId = Number(selectedAssignmentId);
            if (!isHeldReleaseTargetAllowed(detail?.releaseTargets ?? [], selectedAssignmentId)) {
                throw new Error("Selected assignment is not available for this held photo");
            }
            const response = await http(`/api/review/held/${mediaOperationId}/release`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    targetAssignmentId,
                    reason: releaseReason.trim(),
                }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(typeof payload.error === "string" ? payload.error : "Release failed");
            }
            return payload;
        },
        onSuccess: async (payload) => {
            const alreadyReleased = payload?.data?.alreadyReleased === true;
            toast({
                title: alreadyReleased ? "Already released" : "Released to store",
                description: alreadyReleased
                    ? "This photo is already attached to the store task."
                    : "Photo attached to the store task. Approve or reject it in Campaign Tracker when the visit is ready for review.",
            });
            await invalidateReviewQueries();
            onClose();
        },
        onError: (releaseError: Error) => {
            toast({
                title: "Release failed",
                description: releaseError.message,
                variant: "destructive",
            });
        },
    });

    const abandonMutation = useMutation({
        mutationFn: async () => {
            const response = await http(`/api/review/held/${mediaOperationId}/abandon`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ reason: abandonReason.trim() }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(typeof errorData.error === "string" ? errorData.error : "Abandon failed");
            }
            return response.json();
        },
        onSuccess: async () => {
            toast({ title: "Held evidence abandoned" });
            await invalidateReviewQueries();
            setAbandonOpen(false);
            onClose();
        },
        onError: (abandonError: Error) => {
            toast({
                title: "Abandon failed",
                description: abandonError.message,
                variant: "destructive",
            });
        },
    });

    const canRelease = canSubmitHeldRelease({
        detail,
        selectedAssignmentId,
        releaseReason,
    });

    const operatorFacts = detail && proposedTask
        ? describeHeldEvidenceOperatorFacts({
            operatorName: detail.operatorName,
            storeName: detail.storeName,
            proposedTask,
        })
        : null;

    const situationSummary = detail && proposedTask
        ? describeHeldEvidenceSituation({
            operatorName: detail.operatorName,
            storeName: detail.storeName,
            heldStatus: detail.heldStatus,
            automaticBindingBlocker: detail.automaticBindingBlocker,
            proposedAssignmentId: detail.proposedAssignmentId,
            assignmentId: detail.assignmentId,
            proposedTask,
        })
        : null;

    const displayCampaignName = detail
        ? heldEvidenceDisplayCampaignName({
            campaignName: detail.campaignName,
            proposedTask: proposedTask ?? undefined,
            proposedAssignmentId: detail.proposedAssignmentId,
        })
        : '';

    const photoReadyForReview = detail?.bytesServerSafe === true;

    return (
        <>
            <Dialog open={!!mediaOperationId} onOpenChange={(open) => !open && onClose()}>
                <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {detail ? heldEvidenceModalTitle(detail.bytesServerSafe) : "Held photo"}
                        </DialogTitle>
                    </DialogHeader>

                    {isLoading && (
                        <div className="flex justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    )}

                    {error && (
                        <Card className="border-destructive">
                            <CardContent className="p-6 text-destructive">
                                Failed to load held evidence.
                            </CardContent>
                        </Card>
                    )}

                    {detail && (
                        <div className="space-y-6">
                            <Card className="border-teal-100 bg-teal-50/40">
                                <CardContent className="pt-5 space-y-4">
                                    <div className="space-y-2">
                                        <h3 className="text-sm font-semibold text-teal-950">
                                            {photoReadyForReview ? "Why does this need your review?" : "Why is this here?"}
                                        </h3>
                                        <p className="text-sm text-teal-900/90">
                                            {photoReadyForReview
                                                ? HELD_EVIDENCE_WHY_HELD_INTRO
                                                : HELD_EVIDENCE_AWAITING_UPLOAD_INTRO}
                                        </p>
                                    </div>

                                    {operatorFacts && (
                                        <div className="space-y-2 border-t border-teal-100 pt-4">
                                            <h3 className="text-sm font-semibold text-teal-950">What the operator did</h3>
                                            <dl className="grid gap-2 text-sm text-teal-900/90 sm:grid-cols-[7rem_1fr]">
                                                <dt className="font-medium text-teal-950/80">Operator</dt>
                                                <dd>{operatorFacts.operator}</dd>
                                                <dt className="font-medium text-teal-950/80">Store</dt>
                                                <dd>{operatorFacts.store}</dd>
                                                <dt className="font-medium text-teal-950/80">Photographed</dt>
                                                <dd className="font-medium text-teal-950">{operatorFacts.photographed}</dd>
                                            </dl>
                                            {photoReadyForReview && proposedTask?.isAvailableForRelease && (
                                                <p className="text-sm text-teal-900/90">
                                                    The operator&apos;s task is still active — attaching to{" "}
                                                    <span className="font-medium text-teal-950">
                                                        {operatorFacts.photographed}
                                                    </span>{" "}
                                                    is the expected choice if the photo is genuine.
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {situationSummary && (
                                        <div className="space-y-2 border-t border-teal-100 pt-4">
                                            <h3 className="text-sm font-semibold text-teal-950">
                                                {photoReadyForReview ? "What happened" : "Current status"}
                                            </h3>
                                            <p className="text-sm text-teal-900/90">{situationSummary}</p>
                                        </div>
                                    )}

                                    {photoReadyForReview ? (
                                        <div className="space-y-2 border-t border-teal-100 pt-4">
                                            <h3 className="text-sm font-semibold text-teal-950">Your decision</h3>
                                            <ul className="space-y-2 text-sm text-teal-900/90 list-none">
                                                <li>{HELD_EVIDENCE_DECISION_RELEASE}</li>
                                                <li>{HELD_EVIDENCE_DECISION_ABANDON}</li>
                                            </ul>
                                            <p className="text-sm text-muted-foreground">{HELD_EVIDENCE_RELEASE_HELP}</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2 border-t border-teal-100 pt-4">
                                            <h3 className="text-sm font-semibold text-teal-950">What should I do?</h3>
                                            <p className="text-sm text-teal-900/90">{HELD_EVIDENCE_AWAITING_UPLOAD_NEXT}</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <Card>
                                <CardContent className="pt-5 space-y-4">
                                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                        <div className="space-y-2">
                                            <h2 className="text-xl font-semibold">{detail.storeName}</h2>
                                            <div className="flex flex-wrap gap-2">
                                                <Badge variant="outline">{displayCampaignName}</Badge>
                                                <Badge className="bg-teal-100 text-teal-900 border border-teal-200 hover:bg-teal-100">
                                                    {photoReadyForReview ? "Needs review" : "Upload pending"}
                                                </Badge>
                                                <Badge variant="secondary">
                                                    {heldEvidenceReviewStatusLabel(detail.heldStatus)}
                                                </Badge>
                                                <Badge
                                                    variant="outline"
                                                    className={detail.bytesServerSafe
                                                        ? "bg-emerald-50 text-emerald-900 border-emerald-200"
                                                        : undefined}
                                                >
                                                    {heldEvidenceUploadBadgeLabel(detail.bytesServerSafe)}
                                                </Badge>
                                            </div>
                                        </div>
                                        <div className="grid gap-2 text-sm text-muted-foreground md:text-right">
                                            <div className="flex items-center gap-2 md:justify-end">
                                                <User className="h-4 w-4" />
                                                <span>{detail.operatorName}</span>
                                            </div>
                                            <div className="flex items-center gap-2 md:justify-end">
                                                <Calendar className="h-4 w-4" />
                                                <span>
                                                    Captured {format(new Date(detail.capturedAt), "MMM d, yyyy h:mm a")}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2 md:justify-end">
                                                <MapPin className="h-4 w-4" />
                                                <span>Store {detail.storeId} · Cycle {detail.cycleId}</span>
                                            </div>
                                            {detail.uploadedAt && (
                                                <div className="text-xs">
                                                    Uploaded {formatDistanceToNow(new Date(detail.uploadedAt), { addSuffix: true })}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {proposedTask && heldEvidenceBlockerMessage(detail.automaticBindingBlocker, proposedTask) && (
                                        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                                            {heldEvidenceBlockerMessage(detail.automaticBindingBlocker, proposedTask)}
                                        </p>
                                    )}
                                </CardContent>
                            </Card>

                            {detail.imageUrl ? (
                                <div className="rounded-lg border overflow-hidden bg-muted/30">
                                    <img
                                        src={detail.imageUrl}
                                        alt="Held evidence"
                                        className="w-full max-h-[420px] object-contain bg-black/5"
                                    />
                                </div>
                            ) : (
                                <Card className="border-dashed">
                                    <CardContent className="py-8 text-center space-y-2">
                                        <p className="text-sm font-medium text-foreground">No photo to review yet</p>
                                        <p className="text-sm text-muted-foreground max-w-lg mx-auto">
                                            {HELD_EVIDENCE_PREVIEW_PENDING}
                                        </p>
                                    </CardContent>
                                </Card>
                            )}

                            {photoReadyForReview && (
                            <Card>
                                <CardContent className="pt-5 space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="held-release-assignment">Attach photo to task</Label>
                                        <Select
                                            value={selectedAssignmentId}
                                            onValueChange={setSelectedAssignmentId}
                                            disabled={detail.releaseTargets.length === 0}
                                        >
                                            <SelectTrigger id="held-release-assignment">
                                                <SelectValue placeholder="Choose which task this photo belongs to" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {detail.releaseTargets.map(target => (
                                                    <SelectItem
                                                        key={target.assignmentId}
                                                        value={String(target.assignmentId)}
                                                    >
                                                        {target.campaignName} · #{target.assignmentId} · {target.status}
                                                        {target.recommended ? " (recommended)" : ""}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="held-release-reason">Release reason</Label>
                                        <Textarea
                                            id="held-release-reason"
                                            value={releaseReason}
                                            onChange={(event) => setReleaseReason(event.target.value)}
                                            rows={3}
                                        />
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            onClick={() => releaseMutation.mutate()}
                                            disabled={!canRelease || releaseMutation.isPending}
                                        >
                                            {releaseMutation.isPending ? (
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <ShieldCheck className="h-4 w-4 mr-2" />
                                            )}
                                            Release to store
                                        </Button>
                                        <Button
                                            variant="destructive"
                                            onClick={() => setAbandonOpen(true)}
                                            disabled={abandonMutation.isPending}
                                        >
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            Discard photo
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <AlertDialog open={abandonOpen} onOpenChange={setAbandonOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard this held photo?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes the photo from the held queue and saves your reason. It will not be attached to any store task.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-2 py-2">
                        <Label htmlFor="held-abandon-reason">Reason</Label>
                        <Textarea
                            id="held-abandon-reason"
                            value={abandonReason}
                            onChange={(event) => setAbandonReason(event.target.value)}
                            rows={3}
                            placeholder="Why should this photo be discarded?"
                        />
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={!abandonReason.trim() || abandonMutation.isPending}
                            onClick={(event) => {
                                event.preventDefault();
                                abandonMutation.mutate();
                            }}
                        >
                            Abandon photo
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
